import React, { useEffect, useState } from "react";
import { collection, query, orderBy, getDocs } from "firebase/firestore";
import { db } from "../config/firebase";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate, useLocation } from "react-router-dom";
import { useLearning } from "../contexts/LearningContext";

interface LearningItem {
  id: string;
  docId: string;
  title: string;
  emoji: string;
  timestamp: Date;
  hasContent: {
    summary: boolean;
    quiz: boolean;
    flashcards: boolean;
  };
}

const LearningHistory: React.FC = () => {
  const { user } = useAuth();
  const { learningUpdated } = useLearning();
  const [learnings, setLearnings] = useState<LearningItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const currentLearningId = location.pathname.split("/learning/")[1];

  useEffect(() => {
    const fetchLearningHistory = async () => {
      if (!user) return;
      setIsLoading(true);
      setError(null);
      try {
        const historyRef = collection(db, "users", user.uid, "learningHistory");
        const q = query(historyRef, orderBy("timestamp", "desc"));
        const querySnapshot = await getDocs(q);
        const items: LearningItem[] = [];
        querySnapshot.forEach((doc) => {
          const data = doc.data();
          items.push({
            id: data.id,
            docId: doc.id,
            title: data.title,
            emoji: data.emoji,
            timestamp: data.timestamp.toDate(),
            hasContent: data.hasContent,
          });
        });
        setLearnings(items);
      } catch (err) {
        setError("Failed to load history");
      } finally {
        setIsLoading(false);
      }
    };
    fetchLearningHistory();
  }, [user, learningUpdated]);

  // Group by date label
  const groupedLearnings = React.useMemo(() => {
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const sevenDaysAgo = new Date(today);
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);

    const groups: { label: string; items: LearningItem[] }[] = [];
    const todayItems: LearningItem[] = [];
    const yesterdayItems: LearningItem[] = [];
    const weekItems: LearningItem[] = [];
    const olderItems: LearningItem[] = [];

    learnings.forEach((item) => {
      const d = item.timestamp;
      if (d.toDateString() === today.toDateString()) {
        todayItems.push(item);
      } else if (d.toDateString() === yesterday.toDateString()) {
        yesterdayItems.push(item);
      } else if (d >= sevenDaysAgo) {
        weekItems.push(item);
      } else {
        olderItems.push(item);
      }
    });

    if (todayItems.length) groups.push({ label: "Today", items: todayItems });
    if (yesterdayItems.length) groups.push({ label: "Yesterday", items: yesterdayItems });
    if (weekItems.length) groups.push({ label: "Past 7 days", items: weekItems });
    if (olderItems.length) groups.push({ label: "Older", items: olderItems });

    return groups;
  }, [learnings]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-28">
        <div className="w-4 h-4 border-2 border-t-[#47b3fe] border-slate-200 dark:border-gray-700 rounded-full animate-spin"></div>
      </div>
    );
  }

  if (error) {
    return (
      <p className="text-xs text-red-400 text-center px-2 py-4">{error}</p>
    );
  }

  if (learnings.length === 0) {
    return (
      <div className="flex flex-col items-center text-center px-4 py-8 gap-3">
        <div className="w-9 h-9 rounded-xl bg-slate-100 dark:bg-gray-900 border border-slate-200 dark:border-gray-800 flex items-center justify-center">
          <i className="fas fa-book-open text-slate-300 dark:text-gray-700 text-sm"></i>
        </div>
        <div>
          <p className="text-sm font-medium text-slate-500 dark:text-slate-500">
            No sessions yet
          </p>
          <p className="text-xs text-slate-400 dark:text-slate-600 mt-0.5">
            Generate your first topic above
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full overflow-y-auto custom-scrollbar -mx-1 px-1 gap-4">
      {groupedLearnings.map((group) => (
        <div key={group.label}>
          <p className="text-[10px] font-semibold text-slate-400 dark:text-gray-600 uppercase tracking-wider px-2 mb-1">
            {group.label}
          </p>
          <ul className="space-y-0.5">
            {group.items.map((item) => {
              const isActive = item.docId === currentLearningId;
              return (
                <li key={item.docId}>
                  <button
                    onClick={() => navigate(`/home/learning/${item.docId}`)}
                    title={item.title}
                    className={`w-full flex items-center gap-2.5 px-2.5 py-2 rounded-xl transition-all duration-150 text-left group
                      ${isActive
                        ? "bg-white dark:bg-gray-900 shadow-card border border-slate-200 dark:border-gray-800"
                        : "hover:bg-white dark:hover:bg-gray-900 border border-transparent hover:border-slate-100 dark:hover:border-gray-800"
                      }`}
                  >
                    {/* Colored dot or emoji initial */}
                    <div className={`w-6 h-6 rounded-lg flex items-center justify-center flex-shrink-0 text-xs
                      ${isActive
                        ? "bg-[#47b3fe]/10 dark:bg-[#47b3fe]/10"
                        : "bg-slate-100 dark:bg-gray-800 group-hover:bg-slate-200 dark:group-hover:bg-gray-700"
                      }`}
                    >
                      <span className="text-[11px] leading-none">{item.emoji}</span>
                    </div>

                    <span
                      className={`text-sm truncate leading-tight flex-1 ${
                        isActive
                          ? "font-semibold text-slate-900 dark:text-white"
                          : "font-medium text-slate-600 dark:text-slate-400 group-hover:text-slate-800 dark:group-hover:text-slate-200"
                      }`}
                    >
                      {item.title}
                    </span>

                    {/* Content indicators */}
                    {isActive && (
                      <div className="flex gap-1 flex-shrink-0">
                        {item.hasContent.summary && (
                          <span className="w-1.5 h-1.5 rounded-full bg-[#47b3fe]"></span>
                        )}
                        {item.hasContent.quiz && (
                          <span className="w-1.5 h-1.5 rounded-full bg-indigo-400"></span>
                        )}
                      </div>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      ))}
    </div>
  );
};

export default LearningHistory;
